import React from "react";
import ExlyModal from "common/Components/ExlyModal";
import ShieldOutlinedIcon from "@material-ui/icons/Security";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import ExlyLoader from "ui/modules/ExlyLoader";
import { Checkbox } from "@my-scoot/component-library-legacy";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import {
  CUSTOMER_DETAILS_KEYS,
  FIELDS_SCHEMA,
  GST_DETAILS_KEYS,
  INVOICE_DETAILS_KEYS,
  WORKFLOWS,
  GST_PERCENTAGE_OPTIONS,
} from "./EditInvoiceModal.constants";
import { useEditInvoiceModal } from "./EditInvoiceModal.hooks";
import { indian_states_and_uts_dropdown_options } from "features/Common/constants/indianState";
import { ExlyInput, ExlyDropdown, ExlyPhoneInput } from "common/form";
import ExlySearchInput from "common/form/ExlySearchInput";
import { walletKeys } from "features/Account/modules/Wallets/constants/Wallets.constants";
import "react-phone-input-2/lib/style.css";

/**
 * EditInvoiceModal Component.
 * Implements the modal UI with two workflow variations:
 * 1. E-Invoice workflow: Initiated if the invoice is e-invoice enabled and status is success. Shows warning banner and proceeds to edit/credit note creation.
 * 2. Normal workflow: Prompts the user with a GST filed question first:
 *    - Yes -> Routes to credit note workflow.
 *    - No -> Normal edit flow.
 *
 * Supports inline validation, a review/comparison state, and background query invalidation upon save.
 */
export const EditInvoiceModal = ({ open, onClose, record }) => {
  const {
    formData,
    handleChange,
    isReviewState,
    setIsReviewState,
    originalValues,
    changedFields,
    handleSaveClick,
    workflow,
    banner,
    gstFiledAnswer,
    setGstFiledAnswer,
    isSaving,
    errors,
    handleProceed,
    useWalletBalance,
    setUseWalletBalance,
    walletBalance,
    originalGst,
    updatedGst,
    baseAmount,
    originalGstAmt,
    newGstAmt,
    totalRefundAmount,
    isGstLower,
    searchHSNCode,
    handleHsnSearchChange,
    showHSNInput,
    setShowHSNInput,
    refundValidationData,
    isValidatingRefund,
  } = useEditInvoiceModal(record, open, onClose);

  // isQuestionState: Renders the filing status check screen ("Has GST been filed?").
  const isQuestionState =
    workflow === WORKFLOWS.NORMAL_FLOW && gstFiledAnswer === null;
  // isEInvoiceOrCreditNoteFlow: Indicator showing if credit note or structured update flow is active.
  const isEInvoiceOrCreditNoteFlow =
    workflow === WORKFLOWS.EINVOICE_FLOW || gstFiledAnswer === true;

  // IRN: Government e-invoice Invoice Reference Number.
  const IRN = record?.irn_number;

  const renderField = (field) => {
    const isReadOnly = field.readOnly;
    return (
      <div key={field.key} className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-[14px] tw-font-medium tw-text-[#272522]">
            {field.label}
          </span>

          {isReadOnly && (
            <span className="tw-inline-flex tw-items-center tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-100 tw-px-1.5 tw-py-0.5 tw-text-[9px] tw-font-bold tw-uppercase tw-leading-none tw-text-gray-500">
              Read Only
            </span>
          )}
        </div>
        {field.key === "phone" ? (
          <ExlyPhoneInput
            country={"in"}
            input={{
              name: "phone",
              value: formData.phone || "",
              onChange: (val) => handleChange("phone", val),
            }}
            meta={{
              error: errors.phone,
              touched: Boolean(errors.phone),
            }}
            disabled={isReadOnly}
            placeholder="Enter phone/whatsApp number"
          />
        ) : field.key === "state" ? (
          <ExlyDropdown
            input={{
              name: "state",
              value: formData.state ? String(formData.state) : "",
              onChange: () => {},
            }}
            onChange={(val) => handleChange("state", val)}
            meta={{
              error: errors.state,
              touched: Boolean(errors.state),
            }}
            disabled={isReadOnly}
            options={indian_states_and_uts_dropdown_options}
            placeholder={
              indian_states_and_uts_dropdown_options.find(
                (opt) => String(opt.value) === String(formData.state)
              )?.label || "Select State"
            }
            fullWidth
            size="small"
          />
        ) : (
          <ExlyInput
            type={field.type}
            input={{
              name: field.key,
              value: formData[field.key] || "",
              onChange: () => {},
            }}
            onChange={(val) => handleChange(field.key, val)}
            meta={{
              error: errors[field.key],
              touched: Boolean(errors[field.key]),
            }}
            disabled={isReadOnly}
            fullWidth
            placeholder={
              isReadOnly
                ? `${field.label}`
                : `Enter ${field.label.toLowerCase()}`
            }
            size="small"
          />
        )}
      </div>
    );
  };

  const modalTitle = isReviewState
    ? "Review changes"
    : isQuestionState
    ? "GST Filing Status"
    : "Edit invoice";

  const getPrimaryBtnText = () => {
    if (isReviewState) {
      if (isSaving) return "Saving...";
      if (isGstLower) {
        return useWalletBalance
          ? "Save & Refund via Wallet"
          : "Pay & Save Changes";
      }
      return "Save Changes";
    }
    if (isQuestionState) return undefined;
    return isEInvoiceOrCreditNoteFlow ? "Proceed" : "Review changes";
  };

  const primaryBtnOnClick = isReviewState
    ? handleSaveClick
    : isQuestionState
    ? undefined
    : handleProceed;

  const primaryBtnProps = isReviewState
    ? {
        disabled: checkIsArrayEmpty(changedFields) || isSaving,
        loading: isSaving,
      }
    : undefined;

  const secondaryBtnText = isReviewState ? "Back" : "Cancel";

  const secondaryBtnOnClick = isReviewState
    ? () => setIsReviewState(false)
    : onClose;

  const secondaryBtnProps = isReviewState ? { disabled: isSaving } : undefined;

  return (
    <ExlyModal
      open={open}
      title={modalTitle}
      onClose={onClose}
      primaryBtnText={getPrimaryBtnText()}
      onPrimaryBtnClick={primaryBtnOnClick}
      primaryBtnProps={primaryBtnProps}
      showSecondaryBtn={!isQuestionState}
      secondaryBtnText={secondaryBtnText}
      onSecondaryBtnClick={secondaryBtnOnClick}
      secondaryBtnProps={secondaryBtnProps}
      customFooter={isQuestionState ? <></> : undefined}
      modal_props={{
        modalPaperClassName: "tw-w-[677px] tw-max-w-[677px]",
      }}
      mobile_modal_props={{}}
      className="p-0"
    >
      <>
        {banner && !isReviewState && !isQuestionState && (
          <div
            className={`tw-flex tw-items-start tw-justify-start tw-gap-2.5 tw-p-3 tw-text-sm ${banner.className}`}
          >
            <InfoIcon className="tw-text-base tw-mt-[1px] tw-flex-shrink-0 !tw-text-[18px]" />
            <span className="tw-font-medium">{banner.text}</span>
          </div>
        )}
        <div className="tw-font-sans tw-px-[24px] tw-pb-[24px] tw-pt-[24px]">
          {isReviewState ? (
            <div className="tw-flex tw-flex-col tw-gap-4">
              {isSaving ? (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-gap-3">
                  <ExlyLoader showText={false} />
                  <span className="tw-text-sm tw-font-medium tw-text-gray-600">
                    Saving changes...
                  </span>
                </div>
              ) : checkIsArrayEmpty(changedFields) ? (
                <div className="tw-text-center tw-py-8 tw-text-sm tw-text-gray-400 tw-font-medium">
                  No changes detected.
                </div>
              ) : (
                <>
                  <div className="tw-text-sm tw-text-gray-500 tw-mb-2">
                    Please review the changes before saving. Modified fields are
                    highlighted below:
                  </div>
                  <div className="tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                    {/* Header */}
                    <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-px-4 tw-py-2.5 tw-bg-gray-50 tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-200 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-400">
                      <div>Field</div>
                      <div className="tw-text-red-500">Current Value</div>
                      <div className="tw-text-green-600">New Value</div>
                    </div>

                    {/* Change rows */}
                    <div className="tw-divide-y tw-divide-gray-100 tw-divide-solid">
                      {changedFields.map((field) => (
                        <div
                          key={field.key}
                          className="tw-grid tw-grid-cols-3 tw-gap-4 tw-px-4 tw-py-3 tw-text-xs tw-items-center"
                        >
                          <div className="tw-font-semibold tw-text-gray-700">
                            {field.label}
                          </div>
                          <div className="tw-text-red-600 tw-bg-red-50/50 tw-px-2 tw-py-1 tw-rounded tw-font-mono tw-break-all">
                            {originalValues[field.key] || "—"}
                          </div>
                          <div className="tw-text-green-700 tw-bg-green-50/50 tw-px-2 tw-py-1 tw-rounded tw-font-mono tw-break-all">
                            {formData[field.key] || "—"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {isGstLower && (
                    <div className="tw-mt-4 tw-p-4 tw-bg-[#f9f8ff] tw-rounded-lg tw-border tw-border-solid tw-border-[#C3BEEA] tw-flex tw-flex-col tw-gap-3">
                      <div className="tw-text-xs tw-font-bold tw-uppercase tw-text-[#493AB1] tw-tracking-wider">
                        Tax Difference Refund Calculation
                      </div>
                      {isValidatingRefund ? (
                        <div className="tw-flex tw-items-center tw-justify-center tw-py-4">
                          <ExlyLoader showText={false} />
                        </div>
                      ) : (
                        <div className="tw-flex tw-flex-col tw-gap-1.5 tw-text-sm">
                          <div className="tw-flex tw-justify-between">
                            <span className="tw-text-gray-500">
                              Base Taxable Value
                            </span>
                            <span className="tw-font-semibold tw-text-gray-800">
                              ₹{baseAmount.toFixed(2)}
                            </span>
                          </div>
                          <div className="tw-flex tw-justify-between">
                            <span className="tw-text-gray-500">
                              Original GST ({originalGst}%)
                            </span>
                            <span className="tw-font-semibold tw-text-gray-800">
                              ₹{originalGstAmt.toFixed(2)}
                            </span>
                          </div>
                          <div className="tw-flex tw-justify-between">
                            <span className="tw-text-gray-500">
                              Updated GST ({updatedGst}%)
                            </span>
                            <span className="tw-font-semibold tw-text-gray-800">
                              ₹{newGstAmt.toFixed(2)}
                            </span>
                          </div>

                          {refundValidationData && (
                            <>
                              <div className="tw-w-full tw-h-[1px] tw-bg-[#C3BEEA]/50 tw-my-1" />
                              <div className="tw-flex tw-justify-between">
                                <span className="tw-text-gray-500">
                                  Amount to be refunded
                                </span>
                                <span className="tw-font-semibold tw-text-gray-800">
                                  ₹
                                  {parseFloat(
                                    refundValidationData.refund_amount || 0
                                  ).toFixed(2)}
                                </span>
                              </div>
                              <div className="tw-flex tw-justify-between">
                                <span className="tw-text-gray-500">
                                  Refund handling fees
                                </span>
                                <span className="tw-font-semibold tw-text-gray-800">
                                  ₹
                                  {parseFloat(
                                    refundValidationData.rihf_fees_exclusive_of_tx ??
                                      0
                                  ).toFixed(2)}
                                </span>
                              </div>
                              <div className="tw-flex tw-justify-between">
                                <span className="tw-text-gray-500">GST</span>
                                <span className="tw-font-semibold tw-text-gray-800">
                                  ₹
                                  {parseFloat(
                                    refundValidationData.tax_amount || 0
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}

                          <div className="tw-w-full tw-h-[1.5px] tw-bg-[#C3BEEA] tw-my-1" />
                          <div className="tw-flex tw-justify-between tw-text-base tw-font-bold">
                            <span className="tw-text-gray-900">
                              Total Refund Amount
                            </span>
                            <span className="tw-text-green-600">
                              ₹{totalRefundAmount.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="tw-flex tw-items-center tw-leading-[14.32px] tw-py-[4px] tw-mt-3 tw-text-black tw-w-full">
                        <span className="tw-text-[14px] tw-flex tw-items-center tw-gap-[10px]">
                          <Checkbox
                            size="large"
                            wrapperClassName="!tw-border-[1.5px] !tw-border-solid !tw-border-[#493AB1] !tw-w-[17px] !tw-h-[17px] tw-cursor-pointer"
                            disabled={
                              !walletBalance?.[walletKeys.walletBalance]
                            }
                            checked={useWalletBalance}
                            onChange={(e) =>
                              setUseWalletBalance(e.target.checked)
                            }
                          />
                          <span className="tw-font-medium tw-text-gray-800 tw-text-sm tw-select-none">
                            Use Exly Wallet balance
                            {walletBalance != null && (
                              <span className="!tw-font-normal tw-text-[#27252299] tw-text-xs tw-ml-1">
                                (Current balance: ₹
                                {Number(
                                  walletBalance[walletKeys.walletBalance] || 0
                                ).toFixed(2)}
                                )
                              </span>
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : isQuestionState ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-py-4 tw-px-2 tw-gap-4">
              <div className="tw-bg-blue-50 tw-p-3 tw-rounded-full">
                <InfoIcon className="tw-text-blue-600 !tw-text-[32px]" />
              </div>
              <div>
                <h3 className="tw-text-base tw-font-semibold tw-text-gray-900">
                  Has GST been filed for this invoice?
                </h3>
                <p className="tw-text-sm tw-text-gray-500 tw-max-w-md tw-leading-relaxed">
                  Please confirm if you have already filed GSTR-1 or reported
                  GST for this transaction.
                </p>
              </div>
              <div className="tw-flex tw-gap-4 tw-w-full tw-mt-4">
                <button
                  type="button"
                  onClick={() => setGstFiledAnswer(false)}
                  className="tw-flex-1 tw-py-3 tw-px-4 tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-bg-white hover:tw-bg-gray-50 tw-text-gray-700 tw-font-semibold tw-text-sm tw-cursor-pointer focus:tw-outline-none"
                >
                  No, GST not filed
                </button>
                <button
                  type="button"
                  onClick={() => setGstFiledAnswer(true)}
                  className="tw-flex-1 tw-py-3 tw-px-4 tw-border-none tw-bg-[#493ab1] hover:tw-bg-[#3b2e95] tw-text-white tw-rounded-lg tw-font-semibold tw-text-sm tw-cursor-pointer focus:tw-outline-none"
                >
                  Yes, GST filed
                </button>
              </div>
            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-6">
              {/* Customer Details */}
              <div className="tw-flex tw-flex-col tw-gap-4">
                <div className="tw-flex tw-flex-col tw-gap-4">
                  {FIELDS_SCHEMA.filter((f) =>
                    CUSTOMER_DETAILS_KEYS.includes(f.key)
                  ).map(renderField)}
                </div>
              </div>

              <div className="tw-w-full tw-h-px tw-bg-gray-200 tw-my-2" />

              {/* Invoice Details */}
              <div className="tw-flex tw-flex-col tw-gap-4">
                <div className="tw-flex tw-flex-col tw-gap-4">
                  {FIELDS_SCHEMA.filter((f) =>
                    INVOICE_DETAILS_KEYS.includes(f.key)
                  ).map(renderField)}
                </div>
              </div>

              <div className="tw-w-full tw-h-px tw-bg-gray-200 tw-my-2" />

              {/* GST Details */}
              <div className="tw-flex tw-flex-col tw-gap-4">
                <div className="tw-flex tw-flex-col tw-gap-4">
                  {FIELDS_SCHEMA.filter((f) =>
                    GST_DETAILS_KEYS.includes(f.key)
                  ).map(renderField)}

                  {/* HSN & GST % Section adapted from Merchandise */}
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-mt-2">
                    {!showHSNInput ? (
                      <div className="tw-flex tw-flex-col tw-gap-2">
                        <span className="tw-text-[14px] tw-font-medium tw-text-[#272522]">
                          HSN Code
                        </span>
                        <ExlySearchInput
                          fetchSearchData={searchHSNCode}
                          labelField="hsn_code"
                          descriptionField="description"
                          placeholder="Search HSN Code..."
                          size="small"
                          input={{
                            name: "hsnSearch",
                            value: formData.hsnCode
                              ? { hsn_code: formData.hsnCode, description: "" }
                              : null,
                            onChange: handleHsnSearchChange,
                          }}
                          meta={{
                            error: errors.hsnCode,
                            touched: Boolean(errors.hsnCode),
                          }}
                        />
                        {formData.gstPercentage ? (
                          <div className="tw-text-xs tw-text-green-600 tw-font-medium">
                            {formData.gstPercentage}% GST applicable
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="tw-flex tw-flex-col tw-gap-4">
                        <div className="tw-flex tw-flex-col tw-gap-2">
                          <span className="tw-text-[14px] tw-font-medium tw-text-[#272522]">
                            HSN Code
                          </span>
                          <ExlyInput
                            type="number"
                            input={{
                              name: "hsnCode",
                              value: formData.hsnCode || "",
                              onChange: () => {},
                            }}
                            onChange={(val) => handleChange("hsnCode", val)}
                            meta={{
                              error: errors.hsnCode,
                              touched: Boolean(errors.hsnCode),
                            }}
                            fullWidth
                            placeholder="Enter HSN Code"
                            size="small"
                          />
                        </div>

                        <div className="tw-flex tw-flex-col tw-gap-2">
                          <span className="tw-text-[14px] tw-font-medium tw-text-[#272522]">
                            GST Percentage
                          </span>
                          <ExlyDropdown
                            input={{
                              name: "gstPercentage",
                              value:
                                formData.gstPercentage !== undefined &&
                                formData.gstPercentage !== null
                                  ? String(formData.gstPercentage)
                                  : "",
                              onChange: () => {},
                            }}
                            onChange={(val) =>
                              handleChange("gstPercentage", val)
                            }
                            meta={{
                              error: errors.gstPercentage,
                              touched: Boolean(errors.gstPercentage),
                            }}
                            disabled={false}
                            options={GST_PERCENTAGE_OPTIONS}
                            placeholder={
                              GST_PERCENTAGE_OPTIONS.find(
                                (opt) =>
                                  String(opt.value) ===
                                  String(formData.gstPercentage)
                              )?.label || "Select GST Percentage"
                            }
                            fullWidth
                            size="small"
                          />
                        </div>

                        <div className="tw-text-right tw-text-xs">
                          <span
                            className="tw-text-[#493ab1] tw-font-semibold tw-cursor-pointer hover:tw-underline"
                            onClick={() => setShowHSNInput(false)}
                          >
                            Click Here to Search HSN
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Compliance Identifiers */}
              {IRN && (
                <>
                  <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-gray-200">
                    {/* Header */}
                    <div className="tw-flex tw-items-center tw-gap-2 tw-bg-gray-100 tw-px-4 tw-py-3 tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-200">
                      <ShieldOutlinedIcon className="tw-text-[10px] tw-h-2 tw-w-2 tw-text-gray-500" />
                      <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-600">
                        Compliance Identifiers
                      </span>
                    </div>

                    {/* Content */}
                    <div className="tw-flex tw-flex-col">
                      <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
                        <span className="tw-text-sm tw-font-medium tw-text-gray-600">
                          IRN
                        </span>
                        <span className="tw-text-sm tw-font-semibold tw-text-gray-900">
                          {IRN || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </>
    </ExlyModal>
  );
};
